/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path apihooks
 * @description Notification about MyTasks
 * @section 4.2.1.4
 */
import axios from "axios";
import { ICardElementWithChildren } from "backend/model/model";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useMeaQuery from "qpr-base/apihooks/useMeaQuery";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import useCurrentUser from "qpr-base/context/UserContext";
import useAdminRights from "../misc/useAdminRights";
import {
  filterTaskListByKpiPuserBuser,
  getKpiTagSourceTextFromElement,
  shouldSkipKpiPuserBuserFilter,
} from "utils/workflowKpiUserTags";

const useNotificationBell = (executeQuery: boolean = true) => {
  const qc = useQueryClient();
  const ctx = useAppContext();
  const user = useCurrentUser();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname.toLowerCase() : "";
  const shouldSkipOnWorkflowRoute =
    pathname.includes("/mytasks") ||
    pathname.includes("/taskworkflow") ||
    pathname.includes("/workflowinput") ||
    pathname.includes("/delegation") ||
    pathname.includes("/completedtasks");
  const shouldSkipOnWorkflowPage =
    ctx.page === "mytasks" ||
    ctx.page === "taskworkflow" ||
    ctx.page === "workflowinput" ||
    ctx.page === "delegation" ||
    ctx.page === "completedtasks";
  const effectiveExecuteQuery =
    executeQuery && !shouldSkipOnWorkflowPage && !shouldSkipOnWorkflowRoute;
  const isAdmin = useAdminRights();
  const isTenantAdmin = user?.profile?.tenantAdmin === true;
  const hasAdminAccess = isAdmin || isTenantAdmin;
  const cachedWorkflowRole =
    typeof window !== "undefined"
      ? sessionStorage.getItem("workflow-role-cache")
      : null;
  const [userWorkflowRole, setUserWorkflowRole] = useState<string | null>(
    user?.profile?.role || cachedWorkflowRole || null
  );

  // Prefer role from user profile, fallback to API only when missing.
  useEffect(() => {
    if (!effectiveExecuteQuery || !ctx.fullModelId) {
      return;
    }

    if (user?.profile?.role) {
      setUserWorkflowRole(user.profile.role);
      if (typeof window !== "undefined") {
        sessionStorage.setItem("workflow-role-cache", user.profile.role);
      }
      return;
    }

    if (!userWorkflowRole) {
      axios
        .get("/workflow/role")
        .then((response) => {
          const role = response?.data?.data?.role || null;
          setUserWorkflowRole(role);
          if (typeof window !== "undefined" && role) {
            sessionStorage.setItem("workflow-role-cache", role);
          }
        })
        .catch(() => {
          setUserWorkflowRole(null);
        });
    }
  }, [effectiveExecuteQuery, ctx.fullModelId, user?.profile?.role, userWorkflowRole]);

  const { data, isLoading, refetch } = useQuery(
    ["notification-bell-my-tasks", ctx.fullModelId],
    () => {
      return axios.get("/workflow/mytasks", {
        params: {
          modelId: ctx.fullModelId,
          include: "all",
        },
      });
    },
    {
      enabled: effectiveExecuteQuery && !!ctx.fullModelId,
      cacheTime: 300 * 60 * 1000,
      staleTime: 300 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  const kpiMeasureIdsForBell = useMemo(() => {
    const tasks = data?.data?.data;
    if (!Array.isArray(tasks)) return [];
    const ids = new Set<string>();
    for (const t of tasks) {
      if (
        t?.measureType === "KPI" &&
        t?.wfShortType === "value" &&
        t?.measureId
      ) {
        ids.add(t.measureId);
      }
    }
    return Array.from(ids).sort();
  }, [data?.data?.data]);

  const kpiBellTagsMeaQuery = useMemo(
    () =>
      kpiMeasureIdsForBell.length > 0
        ? `[${kpiMeasureIdsForBell.join(",")}]`
        : "",
    [kpiMeasureIdsForBell]
  );

  const { data: kpiRowsForBellTags } = useMeaQuery<ICardElementWithChildren[]>(
    kpiBellTagsMeaQuery,
    `id,typename,description,value(series="JS_ATTR",period="latestforseries",as="JS_ATTR")`,
    undefined,
    {
      enabled:
        effectiveExecuteQuery &&
        !!ctx.fullModelId &&
        kpiMeasureIdsForBell.length > 0,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const kpiTagTextByMeasureId = useMemo(() => {
    const m: Record<string, string> = {};
    const rows = kpiRowsForBellTags?.data;
    if (!rows?.length) return m;
    for (const row of rows) {
      if (row?.id) {
        m[row.id] = getKpiTagSourceTextFromElement(row, ctx.language);
      }
    }
    return m;
  }, [kpiRowsForBellTags?.data, ctx.language]);

  // Filter: for admin final-approval role, only final approval tasks; otherwise show all
  const filteredTasks = useMemo(() => {
    if (!effectiveExecuteQuery || isLoading || !data?.data?.data) {
      return [];
    }
    const tasks = data.data.data;

    const userEmail =
      user?.profile?.email ?? (user as { email?: string }).email;
    const userLogonName =
      user?.profile?.meaLogonName ??
      (user as { meaLogonName?: string }).meaLogonName;

    // Admin/tenant-admin: only final approval tasks
    if (hasAdminAccess) {
      const mapped = tasks
        .filter((task: any) => {
          const shortState = task.shortState || "";
          const state = (task.state || "").toLowerCase();
          return (
            shortState === "finalapproval" ||
            state.includes("final approval") ||
            state.includes("finalapproval")
          );
        })
        .map((task: any) => ({ workflowState: task }));
      return filterTaskListByKpiPuserBuser(
        mapped,
        ctx.language,
        userEmail,
        userLogonName,
        hasAdminAccess,
        kpiTagTextByMeasureId
      );
    }

    // If role not loaded, show all (avoid empty badge when loading)
    if (!userWorkflowRole) {
      const mapped = tasks.map((task: any) => ({ workflowState: task }));
      return filterTaskListByKpiPuserBuser(
        mapped,
        ctx.language,
        userEmail,
        userLogonName,
        false,
        kpiTagTextByMeasureId
      );
    }

    // Non-admin users: mirror My Tasks basic role/state filtering
    const mapped = tasks
      .filter((task: any) => {
        const shortState = task.shortState || "";
        const isValueType = task.wfShortType === "value";
        const isResponsible = task.responsible === user.fullname;

        if (userWorkflowRole === "__WF_DATA_INPUT") {
          const hasRole = task.role === "__WF_DATA_INPUT";
          if (isValueType) {
            return (
              (hasRole || isResponsible) &&
              (shortState === "dataentry" || shortState === "approvalrejected")
            );
          }
          return hasRole || isResponsible;
        }

        if (userWorkflowRole === "__WF_DATA_APPROVE") {
          const hasRole = task.role === "__WF_DATA_APPROVE";
          if (isValueType) {
            return (hasRole || isResponsible) && shortState === "approval";
          }
          return hasRole || isResponsible;
        }

        if (userWorkflowRole === "__WF_DATA_CHECKER") {
          const hasRole = task.role === "__WF_DATA_CHECKER";
          if (isValueType) {
            const state = (task.state || "").toLowerCase();
            const matchState =
              shortState === "check" ||
              shortState === "checker" ||
              state.includes("waiting for check");
            return (hasRole || isResponsible) && matchState;
          }
          return hasRole || isResponsible;
        }

        if (userWorkflowRole === "__WF_DATA_FINAL_APPROVE") {
          const hasRole = task.role === "__WF_DATA_FINAL_APPROVE";
          if (isValueType) {
            const state = (task.state || "").toLowerCase();
            const matchState =
              shortState === "finalapproval" ||
              state.includes("final approval") ||
              state.includes("finalapproval");
            return (hasRole || isResponsible) && matchState;
          }
          return hasRole || isResponsible;
        }

        // Default: only responsible tasks
        return isResponsible;
      })
      .map((task: any) => ({ workflowState: task }));

    return filterTaskListByKpiPuserBuser(
      mapped,
      ctx.language,
      userEmail,
      userLogonName,
      shouldSkipKpiPuserBuserFilter({ userWorkflowRole }),
      kpiTagTextByMeasureId
    );
  }, [
    data,
    isLoading,
    effectiveExecuteQuery,
    hasAdminAccess,
    userWorkflowRole,
    user.fullname,
    ctx.language,
    user?.profile?.email,
    (user as { email?: string }).email,
    user?.profile?.meaLogonName,
    (user as { meaLogonName?: string }).meaLogonName,
    kpiTagTextByMeasureId,
  ]);

  const hasNotification = effectiveExecuteQuery
    ? !isLoading && filteredTasks.length > 0
    : null;
  const notificationCount = effectiveExecuteQuery
    ? (!isLoading ? filteredTasks.length : 0)
    : 0;

  const refresh = () => {
    // Commented out on purpose - need to be considered if this is really needed. Causes heavy data fetching from server.
    // qc.invalidateQueries("notification-bell-my-tasks");
    // refetch();
  };
  useEffect(() => {
    if (ctx.fullModelId) {
      refresh();
    }
  }, [ctx.modelId]);
  return { hasNotification, notificationCount, filteredTasks, isLoading, refresh };
};
export default useNotificationBell;
